import { Card, Flex, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconBuildingSkyscraper,
  IconPalette,
  IconPhoto,
  IconUpload,
} from '@tabler/icons-react';
import { useState } from 'react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useAutoBrandTitlePageFromImage } from 'src/secret-menu/auto-brand/hooks/useAutoBrand';
import {
  useGenerateExportAssets,
  useUpdateExportBranding,
  useUploadLogo,
  useUploadTitlePage,
} from 'src/secret-menu/export-assets/hooks/useExportBranding';
import type { AutoBrandData } from 'src/secret-menu/auto-brand/types';

type SummaryRowProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
};

function SummaryRow({ icon, label, value }: SummaryRowProps) {
  return (
    <Flex align="center" gap="md">
      <ThemeIcon variant="light" color="gray" size={40} radius="xl">
        {icon}
      </ThemeIcon>
      <Stack gap={2} flex={1}>
        <Text size="textSm" c="gray.6">
          {label}
        </Text>
        <Text fw={FONT_WEIGHTS.semibold} size="textMd">
          {value}
        </Text>
      </Stack>
    </Flex>
  );
}

function getTitlePageSummary(data: AutoBrandData): string {
  if (data.titlePageMode === 'none') return 'Skipped';

  if (data.titlePageMode === 'template_upload') {
    if (data.titlePageDocx) return `Template + ${data.titlePageDocx.name}`;
    return 'Template + image (no file yet)';
  }

  if (data.titlePageImage) return `Image overlay (${data.titlePageImage.name})`;
  return 'Full image overlay (no image yet)';
}

export default function ReviewSubmitStep(props: StepProps<AutoBrandData>) {
  const { data, previousStep, close } = props;
  const companyId = data.company?.id ?? '';
  const { updateBranding } = useUpdateExportBranding(companyId);
  const { generate } = useGenerateExportAssets(companyId);
  const { uploadFile: uploadTitlePageDocx } = useUploadTitlePage(companyId);
  const { uploadLogo } = useUploadLogo(companyId);
  const { buildFromImage } = useAutoBrandTitlePageFromImage(companyId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const primaryHex = data.config?.color_palette.primary_color ?? '-';

  const titlePageSummary = getTitlePageSummary(data);

  const handleSubmit = async () => {
    if (!data.config) return;
    setIsSubmitting(true);
    try {
      await updateBranding(data.config);
      const { companyLogo, slots } = data.logoUploads;
      for (const slot of ['first_page', 'subsequent_page', 'footer'] as const) {
        const slotState = slots[slot];
        const fileToUpload = slotState.useCompanyLogo ? companyLogo : slotState.file;
        if (fileToUpload) {
          await uploadLogo(slot, fileToUpload);
        }
      }
      await generate();
      if (data.titlePageMode === 'template_upload' && data.titlePageDocx) {
        await uploadTitlePageDocx(data.titlePageDocx);
      }
      if (data.titlePageMode === 'image_overlay' && data.titlePageImage) {
        await buildFromImage(data.titlePageImage);
      }
      notifications.show({
        title: 'Branding saved',
        message: `${data.company?.name} has been branded and deployed.`,
        color: 'green',
      });
      close();
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Something went wrong saving the branding.',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Save & deploy"
      description="We'll save the configuration, regenerate the tenant's export assets, and overwrite any existing files in S3."
      continueText={isSubmitting ? 'Saving…' : 'Save & deploy'}
      showClientName={false}
      hideProgress
      allowVerticalOverflow
      canGoBack={!isSubmitting}
      canGoForward={!isSubmitting && !!data.config}
      previousStep={() => previousStep()}
      nextStep={() => void handleSubmit()}
      isSubmitting={isSubmitting}
    >
      <Stack gap="lg">
        <Card withBorder radius="md" p="lg">
          <Stack gap="md">
            <SummaryRow
              icon={<IconBuildingSkyscraper size={20} />}
              label="Tenant"
              value={
                data.company
                  ? `${data.company.name} - ${data.company.tenantName}`
                  : 'No tenant selected'
              }
            />
            <SummaryRow
              icon={<IconUpload size={20} />}
              label="Source document"
              value={data.sourceFile?.file.name ?? 'Manual configuration'}
            />
            <SummaryRow
              icon={
                <Group gap={4} wrap="nowrap">
                  <IconPalette size={20} />
                </Group>
              }
              label="Primary brand colour"
              value={`#${primaryHex}`}
            />
            <SummaryRow
              icon={<IconPhoto size={20} />}
              label="Title page"
              value={titlePageSummary}
            />
          </Stack>
        </Card>

        <Card withBorder radius="md" p="lg" bg="orange.0">
          <Flex gap="md" align="flex-start">
            <ThemeIcon variant="light" color="orange" size={40} radius="xl">
              <IconAlertTriangle size={20} />
            </ThemeIcon>
            <Stack gap={4}>
              <Text fw={FONT_WEIGHTS.semibold}>This action cannot be undone</Text>
              <Text size="textSm" c="gray.7">
                Clicking save will permanently overwrite{' '}
                <code>companies/{data.company?.tenantName ?? ''}/export_assets.docx</code>
                {data.titlePageMode !== 'none' ? (
                  <>
                    {' '}
                    and <code>companies/{data.company?.tenantName ?? ''}/title_page.docx</code>
                  </>
                ) : null}
                . The existing files will be replaced and cannot be recovered.
              </Text>
            </Stack>
          </Flex>
        </Card>
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
